import { useState, useEffect } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/auth-context';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { 
  ArrowLeft, 
  Briefcase, 
  Building, 
  Calendar, 
  CheckCircle, 
  ClipboardList, 
  FileText, 
  MapPin, 
  Users,
  User
} from 'lucide-react';
import { format } from 'date-fns';
import { CandidateProfileView } from './candidate-profile-view';

interface ApplicationContextProps {
  profileId: string;
}

export function ApplicationContext({ profileId }: ApplicationContextProps) {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [application, setApplication] = useState<any>(null);
  const [job, setJob] = useState<any>(null);
  const [evaluations, setEvaluations] = useState<any[]>([]);
  const [showCandidateProfile, setShowCandidateProfile] = useState(false);

  // Get application context from location state if available
  const fromApplicants = location.state?.fromApplicants || false;
  const jobId = location.state?.jobId;
  const jobTitle = location.state?.jobTitle;
  const applicationId = location.state?.applicationId;

  useEffect(() => {
    if (profileId) {
      if (jobId && applicationId) {
        // If we have jobId and applicationId from state, fetch those specific details
        fetchApplicationDetails(applicationId, jobId);
      } else {
        // Otherwise fetch all applications for this candidate
        fetchCandidateApplications(profileId);
      }
    }
  }, [profileId, jobId, applicationId]);

  const fetchApplicationDetails = async (appId: string, jId: string) => {
    setLoading(true);
    try {
      // Fetch application details
      const { data: appData, error: appError } = await supabase
        .from('applications')
        .select('*')
        .eq('id', appId)
        .single();
        
      if (appError) throw appError;
      setApplication(appData);
      
      // Fetch job details
      const { data: jobData, error: jobError } = await supabase
        .from('recruiting_projects')
        .select(`
          *,
          profiles:profiles(id, full_name, company_name)
        `)
        .eq('id', jId)
        .single();
        
      if (jobError) throw jobError;
      setJob(jobData);
      
      // Fetch evaluations
      fetchEvaluations(appId);
      
    } catch (error) {
      console.error('Error fetching application details:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCandidateApplications = async (pId: string) => {
    setLoading(true);
    try {
      // Fetch all applications for this candidate
      const { data: applications, error: appError } = await supabase
        .from('applications')
        .select(`
          *,
          job:recruiting_projects(
            id,
            title,
            department,
            location,
            profiles(id, company_name)
          )
        `)
        .eq('profile_id', pId)
        .order('application_date', { ascending: false });
        
      if (appError) throw appError;
      
      if (applications && applications.length > 0) {
        // Use the most recent application
        setApplication(applications[0]);
        setJob(applications[0].job);
        
        // Fetch evaluations for this application
        fetchEvaluations(applications[0].id);
      }
      
    } catch (error) {
      console.error('Error fetching candidate applications:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEvaluations = async (appId: string) => {
    try {
      const { data, error } = await supabase
        .from('candidate_evaluations')
        .select(`
          *,
          evaluator:profiles!candidate_evaluations_evaluator_id_fkey(
            id,
            full_name
          )
        `)
        .eq('application_id', appId)
        .order('created_at', { ascending: false });
        
      if (error) throw error;
      setEvaluations(data || []);
      
    } catch (error) {
      console.error('Error fetching evaluations:', error);
    }
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'MMM d, yyyy');
    } catch (e) {
      return 'Unknown date';
    }
  };

  // Status badge variant
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'new':
        return <Badge variant="secondary">New</Badge>;
      case 'reviewed':
        return <Badge variant="outline">Reviewed</Badge>;
      case 'interviewed':
        return <Badge variant="default">Interviewed</Badge>;
      case 'offered':
        return <Badge className="bg-green-500 hover:bg-green-600">Offered</Badge>;
      case 'rejected':
        return <Badge variant="destructive">Rejected</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <Card className="mb-6">
        <CardContent className="p-4 flex justify-center">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent"></div>
        </CardContent>
      </Card>
    );
  }

  if (showCandidateProfile && profileId && job?.id) {
    return (
      <CandidateProfileView 
        profileId={profileId} 
        jobId={job.id} 
        applicationId={application?.id}
        onBack={() => setShowCandidateProfile(false)}
      />
    );
  }

  if (!application || !job) {
    return (
      <Card className="mb-6">
        <CardContent className="p-4">
          <p className="text-muted-foreground text-center">No application data available for this candidate.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mb-6">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => navigate(`/dashboard/applications/${job.id}`)}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <CardTitle className="text-xl">Candidate Application</CardTitle>
              <CardDescription>
                Viewing application for {job.title}
              </CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {getStatusBadge(application.status)}
            <Button 
              variant="default" 
              onClick={() => setShowCandidateProfile(true)}
            >
              <User className="h-4 w-4 mr-2" />
              View Full Profile
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="bg-muted/30 p-4 rounded-lg">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
              <div>
                <h3 className="font-medium flex items-center gap-2">
                  <Briefcase className="h-4 w-4 text-primary" />
                  {job.title}
                </h3>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm mt-1">
                  <span className="flex items-center gap-1">
                    <Building className="h-3.5 w-3.5 text-muted-foreground" />
                    {job.profiles?.company_name || 'Company'}
                  </span>
                  <span className="flex items-center gap-1">
                    <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                    {job.location}
                  </span>
                  <span className="flex items-center gap-1">
                    <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                    Applied on {formatDate(application.application_date)}
                  </span>
                </div>
              </div>
              <div>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="gap-1"
                  asChild
                >
                  <Link to={`/dashboard/applications/${job.id}`}>
                    <Users className="h-4 w-4" />
                    View All Applicants
                  </Link>
                </Button>
              </div>
            </div>
          </div>
          
          <div className="flex flex-col md:flex-row gap-4">
            <Button variant="outline" className="gap-1" size="sm">
              <ClipboardList className="h-4 w-4" />
              Add Evaluation
            </Button>
            <Button variant="outline" className="gap-1" size="sm">
              <Calendar className="h-4 w-4" />
              Schedule Interview
            </Button>
            <Button variant="outline" className="gap-1" size="sm">
              <FileText className="h-4 w-4" />
              Add Notes
            </Button>
          </div>
          
          {evaluations.length > 0 ? (
            <div className="space-y-3 mt-2">
              <h4 className="text-sm font-medium">Evaluations</h4>
              {evaluations.map((evaluation, index) => (
                <div key={index} className="bg-muted/20 p-3 rounded-md">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-sm font-medium">
                        {evaluation.evaluator?.full_name || 'Recruiter'}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {formatDate(evaluation.created_at)}
                      </p>
                    </div>
                    <Badge variant="outline">
                      Overall: {evaluation.overall_rating}/10
                    </Badge>
                  </div>
                  {evaluation.notes && (
                    <p className="text-sm mt-2">{evaluation.notes}</p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-muted/20 p-4 rounded-md text-center">
              <p className="text-sm text-muted-foreground">No evaluations yet</p>
              <p className="text-xs text-muted-foreground mt-1">
                Add an evaluation to track this candidate's progress
              </p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}